import React, { useCallback, useMemo, useState } from 'react';
import { Pencil, X, Check, Loader, RotateCcw, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { updateMaterialityAssessmentReport } from './assessmentReportsEditApi.js';
import {
  reportsEditSensitivityOptions,
  reportsEditBaseAmount,
  reportsEditResolvedThresholds,
  reportsEditBuildMaterialityJson,
  reportsEditSensitivityPercentageLabel,
} from './materialityCalculationEditHelpers.js';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

/**
 * Materiality Calculation edit on Assessment Reports.
 * Edits category, sensitivity, and thresholds; saves via PUT materiality_json.
 */
export default function MaterialityCalculationEditCard({
  assessment,
  materialityData,
  formatCurrency: formatCurrencyProp,
  onSaved,
}) {
  const formatMoney = formatCurrencyProp || formatCurrency;

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState('Total Revenue');
  const [sensitivityLevel, setSensitivityLevel] = useState('Standard');
  const [customCategories, setCustomCategories] = useState([]);
  const [customOverallMateriality, setCustomOverallMateriality] = useState('');
  const [customPlanningMateriality, setCustomPlanningMateriality] = useState('');
  const [customDeminimis, setCustomDeminimis] = useState('');
  const [overrideReasoning, setOverrideReasoning] = useState('');
  const [showReasoningBox, setShowReasoningBox] = useState(false);
  const [editingField, setEditingField] = useState(null);

  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');
  const [customCategoryAmount, setCustomCategoryAmount] = useState('');
  const [customCategoryPercentage, setCustomCategoryPercentage] = useState('');
  const [customModalError, setCustomModalError] = useState('');

  const companyProfile = materialityData?.companyProfile;

  const initFromMateriality = useCallback(() => {
    const m = materialityData || {};
    const confirmed = m.confirmedMateriality || {};
    setSelectedCategory(String(m.selectedCategory || confirmed.selectedCategory || 'Total Revenue'));
    setSensitivityLevel(String(m.sensitivityLevel || confirmed.sensitivityLevel || 'Standard'));
    setCustomCategories(
      Array.isArray(m.customCategories)
        ? m.customCategories
        : Array.isArray(confirmed.customCategories)
          ? confirmed.customCategories
          : []
    );
    const co = confirmed.customOverallMateriality ?? '';
    const cp = confirmed.customPlanningMateriality ?? '';
    const cd = confirmed.customDeminimis ?? '';
    setCustomOverallMateriality(co != null && String(co).trim() !== '' ? String(co) : '');
    setCustomPlanningMateriality(cp != null && String(cp).trim() !== '' ? String(cp) : '');
    setCustomDeminimis(cd != null && String(cd).trim() !== '' ? String(cd) : '');
    const reasoning = String(m.overrideReasoning || confirmed.overrideReasoning || '');
    setOverrideReasoning(reasoning);
    const hasCustom =
      (co != null && String(co).trim() !== '') ||
      (cp != null && String(cp).trim() !== '') ||
      (cd != null && String(cd).trim() !== '');
    setShowReasoningBox(hasCustom || Boolean(reasoning.trim()));
    setEditingField(null);
    setShowCustomModal(false);
    setCustomModalError('');
  }, [materialityData]);

  const startEdit = () => {
    initFromMateriality();
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditingField(null);
    setShowCustomModal(false);
  };

  const resetToPlanDefaults = () => {
    setCustomOverallMateriality('');
    setCustomPlanningMateriality('');
    setCustomDeminimis('');
    setOverrideReasoning('');
    setShowReasoningBox(false);
    setEditingField(null);
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'Custom') {
      setShowCustomModal(true);
      return;
    }
    setSelectedCategory(value);
    setSensitivityLevel('Standard');
    resetToPlanDefaults();
  };

  const handleCreateCustomCategory = () => {
    if (!customCategoryName.trim()) {
      setCustomModalError('Please enter a category name');
      return;
    }
    if (!customCategoryAmount || parseFloat(customCategoryAmount) <= 0) {
      setCustomModalError('Please enter a valid amount');
      return;
    }
    if (!customCategoryPercentage || parseFloat(customCategoryPercentage) <= 0) {
      setCustomModalError('Please enter a valid percentage');
      return;
    }
    const newCategory = {
      name: customCategoryName.trim(),
      amount: parseFloat(String(customCategoryAmount).replace(/,/g, '')),
      percentage: parseFloat(customCategoryPercentage),
    };
    setCustomCategories((prev) => [...prev, newCategory]);
    setSelectedCategory(newCategory.name);
    setSensitivityLevel('Standard');
    resetToPlanDefaults();
    setShowCustomModal(false);
    setCustomCategoryName('');
    setCustomCategoryAmount('');
    setCustomCategoryPercentage('');
    setCustomModalError('');
  };

  const sensitivityOptions = useMemo(
    () => reportsEditSensitivityOptions(selectedCategory, customCategories),
    [selectedCategory, customCategories]
  );

  const thresholds = useMemo(
    () =>
      reportsEditResolvedThresholds({
        companyProfile,
        category: selectedCategory,
        sensitivityLevel,
        customCategories,
        customOverallMateriality,
        customPlanningMateriality,
        customDeminimis,
      }),
    [
      companyProfile,
      selectedCategory,
      sensitivityLevel,
      customCategories,
      customOverallMateriality,
      customPlanningMateriality,
      customDeminimis,
    ]
  );

  const hasMaterialityOverrides = Boolean(
    editingField ||
      showReasoningBox ||
      (customOverallMateriality && String(customOverallMateriality).trim() !== '') ||
      (customPlanningMateriality && String(customPlanningMateriality).trim() !== '') ||
      (customDeminimis && String(customDeminimis).trim() !== '') ||
      (overrideReasoning && String(overrideReasoning).trim() !== '')
  );

  const handleEditClick = (field) => {
    setEditingField(field);
    if (field === 'overall') {
      setCustomOverallMateriality(String(Math.round(thresholds.overallMateriality)));
    } else if (field === 'planning') {
      setCustomPlanningMateriality(String(Math.round(thresholds.planningMateriality)));
    } else if (field === 'deminimis') {
      setCustomDeminimis(String(Math.round(thresholds.deminimis)));
    }
  };

  const handleSaveField = () => {
    setEditingField(null);
    setShowReasoningBox(true);
  };

  const handleSave = async () => {
    if (!assessment?.id || !materialityData) return;
    const hasCustom =
      (customOverallMateriality && String(customOverallMateriality).trim() !== '') ||
      (customPlanningMateriality && String(customPlanningMateriality).trim() !== '') ||
      (customDeminimis && String(customDeminimis).trim() !== '');
    if (hasCustom && !String(overrideReasoning || '').trim()) {
      toast.error('Please provide reasoning for your materiality adjustments');
      setShowReasoningBox(true);
      return;
    }
    try {
      setSaving(true);
      const nextMateriality = reportsEditBuildMaterialityJson(materialityData, {
        selectedCategory,
        sensitivityLevel,
        customCategories,
        customOverallMateriality,
        customPlanningMateriality,
        customDeminimis,
        overrideReasoning,
      });
      const confirmed = nextMateriality?.confirmedMateriality || {};
      const prevEntity = assessment.entityscoping_json;
      const nextEntityScoping =
        prevEntity && typeof prevEntity === 'object'
          ? {
              ...prevEntity,
              editableMateriality: {
                ...(typeof prevEntity.editableMateriality === 'object' && prevEntity.editableMateriality
                  ? prevEntity.editableMateriality
                  : {}),
                overallMateriality: confirmed.overallMateriality,
                planningMateriality: confirmed.planningMateriality,
                deminimis: confirmed.deminimis,
              },
            }
          : prevEntity || null;

      const payload = {
        analysis_name: assessment.analysis_name,
        documents_json: assessment.documents_json || null,
        materiality_json: nextMateriality,
        sox_cycle_json: assessment.sox_cycle_json,
        entityscoping_json: nextEntityScoping,
        ...(assessment.audit_year_id ? { audit_year_id: assessment.audit_year_id } : {}),
      };
      const data = await updateMaterialityAssessmentReport(assessment.id, payload);
      if (!data?.success && !data?.id) {
        throw new Error(data?.error || 'Update failed');
      }
      toast.success('Materiality calculation updated');
      setEditing(false);
      onSaved?.({
        ...assessment,
        ...payload,
        id: assessment.id,
        materiality_json: nextMateriality,
        entityscoping_json: nextEntityScoping,
      });
    } catch (err) {
      toast.error(
        resolve403ErrorMessage(err, err?.response?.data?.error || err.message || 'Failed to update')
      );
    } finally {
      setSaving(false);
    }
  };

  if (!materialityData?.confirmedMateriality) return null;

  const viewCategory = materialityData.selectedCategory;
  const viewSensitivity = materialityData.sensitivityLevel;
  const viewPct = materialityData.sensitivityPercentage;
  const viewOverall = materialityData.confirmedMateriality.overallMateriality;
  const viewPlanning = materialityData.confirmedMateriality.planningMateriality;
  const viewDeminimis = materialityData.confirmedMateriality.deminimis;

  const baseAmount = reportsEditBaseAmount(companyProfile, selectedCategory, customCategories);
  const pctLabel = reportsEditSensitivityPercentageLabel(
    selectedCategory,
    sensitivityLevel,
    customCategories
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Materiality Calculation</h2>
        {!editing ? (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-600 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <>
          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-between gap-4">
              <label className="text-sm font-medium text-gray-600">Selected Category</label>
              <span className="rounded bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-800">
                {viewCategory}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <label className="text-sm font-medium text-gray-600">Sensitivity Level</label>
              <span className="rounded bg-purple-100 px-3 py-1 text-sm font-medium text-purple-800">
                {viewSensitivity}
                {viewPct ? ` (${viewPct})` : ''}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-lg border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-indigo-100 p-6">
              <label className="mb-2 block text-sm font-medium text-indigo-700">Overall Materiality</label>
              <p className="text-3xl font-bold text-indigo-900">{formatMoney(viewOverall)}</p>
            </div>
            <div className="rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 p-6">
              <label className="mb-2 block text-sm font-medium text-purple-700">Planning Materiality</label>
              <p className="text-3xl font-bold text-purple-900">{formatMoney(viewPlanning)}</p>
            </div>
            <div className="rounded-lg border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-pink-100 p-6">
              <label className="mb-2 block text-sm font-medium text-pink-700">De Minimis</label>
              <p className="text-3xl font-bold text-pink-900">{formatMoney(viewDeminimis)}</p>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Category — same options as assessment */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-semibold text-gray-700">Select Materiality Category</h4>
            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Net Income">Net Income (Pre-Tax)</option>
              <option value="Total Revenue">Total Revenue</option>
              <option value="Total Assets">Total Assets</option>
              {customCategories.map((category, index) => (
                <option key={`${category.name}-${index}`} value={category.name}>
                  {category.name} (Custom)
                </option>
              ))}
              <option value="Custom">+ Add Custom Category</option>
            </select>
          </div>

          {/* Sensitivity cards — same as assessment */}
          <div className="mb-8">
            <h4 className="mb-4 text-sm font-semibold text-gray-700">
              <BarChart3 className="mr-2 inline h-4 w-4" />
              Sensitivity Analysis Comparison
            </h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {sensitivityOptions.map((option) => {
                const pct = parseFloat(String(option.percentage).replace('%', '')) / 100;
                const amount = baseAmount * pct;
                const selected = sensitivityLevel === option.label;
                return (
                  <button
                    type="button"
                    key={option.label}
                    onClick={() => {
                      setSensitivityLevel(option.label);
                    }}
                    className={`rounded-lg border-2 p-4 text-left transition-all ${
                      selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="mb-1 text-sm font-medium text-gray-700">{option.label}</p>
                    <p className="text-lg font-bold text-gray-900">{formatMoney(amount)}</p>
                    <p className="text-xs text-gray-600">{option.percentage}</p>
                    {selected && (
                      <span className="mt-2 inline-block rounded bg-blue-600 px-2 py-1 text-xs text-white">
                        Selected
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Thresholds with pencils — same as assessment */}
          <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-gray-800">Materiality thresholds</h3>
            <button
              type="button"
              onClick={resetToPlanDefaults}
              disabled={!hasMaterialityOverrides}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                hasMaterialityOverrides
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'cursor-not-allowed border-gray-200 text-gray-400'
              }`}
            >
              <RotateCcw className="h-4 w-4" />
              Reset Changes
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-3 items-start">
            <div className="flex flex-col">
              <h4 className="mb-2 text-sm font-semibold text-gray-700">Overall Materiality</h4>
              <div className="flex items-center gap-2">
                {editingField === 'overall' ? (
                  <>
                    <input
                      type="number"
                      value={customOverallMateriality}
                      onChange={(e) => setCustomOverallMateriality(e.target.value)}
                      className="w-48 rounded border border-blue-500 px-2 py-1 text-2xl font-semibold text-blue-600"
                      step="1"
                    />
                    <button type="button" onClick={handleSaveField} className="text-green-600 hover:text-green-700">
                      <Check className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-semibold text-blue-600">
                      {formatMoney(thresholds.overallMateriality)}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleEditClick('overall')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
              <p className="mt-1 text-xs leading-tight text-gray-600">
                {customOverallMateriality && String(customOverallMateriality).trim() !== ''
                  ? 'custom value'
                  : `${pctLabel} of ${String(selectedCategory).toLowerCase()} (${formatMoney(baseAmount)}) (${String(sensitivityLevel).toLowerCase()})`}
              </p>
            </div>

            <div className="flex flex-col">
              <h4 className="mb-2 text-sm font-semibold text-gray-700">Planning Materiality</h4>
              <div className="flex items-center gap-2">
                {editingField === 'planning' ? (
                  <>
                    <input
                      type="number"
                      value={customPlanningMateriality}
                      onChange={(e) => setCustomPlanningMateriality(e.target.value)}
                      className="w-48 rounded border border-blue-500 px-2 py-1 text-2xl font-semibold text-gray-900"
                      step="1"
                    />
                    <button type="button" onClick={handleSaveField} className="text-green-600 hover:text-green-700">
                      <Check className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatMoney(thresholds.planningMateriality)}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleEditClick('planning')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
              <p className="mt-1 text-xs leading-tight text-gray-600">
                {customPlanningMateriality && String(customPlanningMateriality).trim() !== ''
                  ? 'custom value'
                  : '50% of Overall'}
              </p>
            </div>

            <div className="flex flex-col">
              <h4 className="mb-2 text-sm font-semibold text-gray-700">Sum De Minimis</h4>
              <div className="flex items-center gap-2">
                {editingField === 'deminimis' ? (
                  <>
                    <input
                      type="number"
                      value={customDeminimis}
                      onChange={(e) => setCustomDeminimis(e.target.value)}
                      className="w-48 rounded border border-blue-500 px-2 py-1 text-2xl font-semibold text-gray-900"
                      step="1"
                    />
                    <button type="button" onClick={handleSaveField} className="text-green-600 hover:text-green-700">
                      <Check className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatMoney(thresholds.deminimis)}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleEditClick('deminimis')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
              <p className="mt-1 text-xs leading-tight text-gray-600">
                {customDeminimis && String(customDeminimis).trim() !== ''
                  ? 'custom value'
                  : '10% of Planning Materiality'}
              </p>
            </div>
          </div>

          {showReasoningBox && (
            <div className="mt-6">
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Reasoning for Materiality Adjustments <span className="text-red-500">*</span>
              </label>
              <textarea
                value={overrideReasoning}
                onChange={(e) => setOverrideReasoning(e.target.value)}
                placeholder="Explain why you adjusted the materiality values... (required)"
                className={`min-h-[120px] w-full rounded-lg border px-4 py-3 focus:outline-none focus:ring-2 ${
                  !String(overrideReasoning || '').trim()
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />
              {!String(overrideReasoning || '').trim() && (
                <p className="mt-1 text-xs text-red-600">
                  Please provide reasoning for your materiality adjustments
                </p>
              )}
            </div>
          )}
        </>
      )}

      {!editing && (materialityData.overrideAmount || materialityData.overrideReasoning) && (
        <div className="mt-6 border-t pt-6">
          <h3 className="mb-3 text-lg font-medium text-gray-800">Materiality Adjustments</h3>
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            {materialityData.overrideAmount && (
              <p className="mb-2 text-sm font-medium text-yellow-800">
                Override Amount: {formatMoney(parseFloat(materialityData.overrideAmount))}
              </p>
            )}
            {materialityData.overrideReasoning && (
              <p className="text-sm text-gray-700">
                <strong>Reasoning:</strong> {materialityData.overrideReasoning}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Add Custom Category modal — same as assessment */}
      {showCustomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Custom Materiality Category</h3>
              <button
                type="button"
                onClick={() => {
                  setShowCustomModal(false);
                  setCustomModalError('');
                  setCustomCategoryName('');
                  setCustomCategoryAmount('');
                  setCustomCategoryPercentage('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {customModalError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">{customModalError}</p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Category Name</label>
                <input
                  type="text"
                  value={customCategoryName}
                  onChange={(e) => setCustomCategoryName(e.target.value)}
                  placeholder="e.g., EBITDA, Gross Profit"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Amount ($)</label>
                <input
                  type="number"
                  value={customCategoryAmount}
                  onChange={(e) => setCustomCategoryAmount(e.target.value)}
                  placeholder="e.g., 50000000"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Percentage (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={customCategoryPercentage}
                  onChange={(e) => setCustomCategoryPercentage(e.target.value)}
                  placeholder="e.g., 5.0"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This will be the &quot;Standard&quot; percentage. Conservative will be half (÷2), Moderate will be
                  double (×2).
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCustomModal(false);
                  setCustomModalError('');
                  setCustomCategoryName('');
                  setCustomCategoryAmount('');
                  setCustomCategoryPercentage('');
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCustomCategory}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
